import { apiFetch } from "../lib/api";
import { HierarchyAccessError, id, object, request, session, type HierarchyScope } from "./api";

export interface ArtifactLocation {
  accountId: string;
  workspaceId: string;
  projectId?: string;
}
export interface CatalogArtifact extends ArtifactLocation {
  id: string;
  title: string;
  kind: "document" | "image" | "file";
  headVersionId: string;
}
export interface ArtifactCatalog {
  scope: HierarchyScope;
  canArchive: boolean;
  artifacts: CatalogArtifact[];
}

function rows(value: unknown): unknown[] {
  if (value === null) return [];
  if (!Array.isArray(value)) throw new HierarchyAccessError();
  return value;
}

async function authorize(location: ArtifactLocation, expected?: HierarchyScope, signal?: AbortSignal) {
  id(location.accountId); id(location.workspaceId);
  if (location.projectId !== undefined) id(location.projectId);
  const scope = await session(signal);
  if (scope.accountId !== location.accountId || (expected && scope.userId !== expected.userId)) throw new HierarchyAccessError();
  const root = `/code/workspaces/${encodeURIComponent(location.workspaceId)}`;
  const workspace = await request(root, signal);
  if (workspace.id !== location.workspaceId || workspace.accountId !== location.accountId) throw new HierarchyAccessError();
  const projects = rows((await request(`${root}/projects`, signal)).projects);
  const projectIDs = new Set<string>();
  for (const raw of projects) {
    const project = object(raw), projectID = id(project.id);
    if (projectIDs.has(projectID) || project.accountId !== location.accountId || project.workspaceId !== location.workspaceId) throw new HierarchyAccessError();
    projectIDs.add(projectID);
  }
  if (location.projectId && !projectIDs.has(location.projectId)) throw new HierarchyAccessError();
  const current = await session(signal);
  if (current.accountId !== scope.accountId || current.userId !== scope.userId) throw new HierarchyAccessError();
  return { scope, root, projectIDs, canArchive: workspace.accessRole === "admin" };
}

// Catalogs are ephemeral. A project page shows its own documents; the workspace
// page shows workspace-owned documents, matching conversation navigation.
export async function loadArtifactCatalog(location: ArtifactLocation, signal?: AbortSignal, expected?: HierarchyScope): Promise<ArtifactCatalog> {
  const access = await authorize(location, expected, signal);
  const response = await request(`${access.root}/artifacts`, signal);
  const seen = new Set<string>();
  const artifacts = rows(response.artifacts).map((raw): CatalogArtifact => {
    const row = object(raw), artifactID = id(row.id);
    const projectId = row.projectId === undefined || row.projectId === "" ? undefined : id(row.projectId);
    if (seen.has(artifactID) || row.accountId !== location.accountId || row.workspaceId !== location.workspaceId ||
      typeof row.title !== "string" || !["document", "image", "file"].includes(String(row.kind)) ||
      (projectId && !access.projectIDs.has(projectId))) throw new HierarchyAccessError();
    seen.add(artifactID);
    return { id: artifactID, accountId: location.accountId, workspaceId: location.workspaceId, projectId,
      title: row.title, kind: row.kind as CatalogArtifact["kind"], headVersionId: id(row.headVersionId) };
  }).filter((row) => row.projectId === location.projectId);
  const current = await authorize(location, access.scope, signal);
  signal?.throwIfAborted();
  return { scope: current.scope, canArchive: current.canArchive, artifacts };
}

// Call only after explicit confirmation. Re-read the catalog before mutation:
// stale titles/versions require a new review, while an already absent record
// needs no repeated DELETE (including a lost successful response).
export async function archiveCatalogArtifact(location: ArtifactLocation, selected: CatalogArtifact, expected: HierarchyScope, signal?: AbortSignal): Promise<ArtifactCatalog> {
  if (selected.accountId !== location.accountId || selected.workspaceId !== location.workspaceId || selected.projectId !== location.projectId) throw new HierarchyAccessError();
  id(selected.id); id(selected.headVersionId);
  const catalog = await loadArtifactCatalog(location, signal, expected);
  if (!catalog.canArchive) throw new HierarchyAccessError();
  const current = catalog.artifacts.find((row) => row.id === selected.id);
  if (!current) return catalog;
  if (current.headVersionId !== selected.headVersionId || current.title !== selected.title || current.kind !== selected.kind) throw new Error("This document changed. Reload and review it before archiving.");
  signal?.throwIfAborted();
  const response = await apiFetch(`/code/workspaces/${encodeURIComponent(location.workspaceId)}/artifacts/${encodeURIComponent(selected.id)}`, {
    method: "DELETE", credentials: "include", cache: "no-store", signal,
  });
  if (response.status !== 204) throw new HierarchyAccessError();
  const refreshed = await loadArtifactCatalog(location, signal, expected);
  if (refreshed.artifacts.some((row) => row.id === selected.id)) throw new HierarchyAccessError();
  return refreshed;
}
